// DraftCanvas — center-pane writing surface for the active piece in /composer.
//
// Three modes:
//   1. No workspace → empty state.
//   2. Real draft exists (user_edit revision or AI draft) → editor or read-only body.
//   3. Otherwise (no piece, or piece without a draft yet) → blank TipTap
//      canvas with localStorage autosave + a "Save as Draft" button that
//      promotes it into a real Brief + user_edit Draft revision.

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, Save as SaveIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DraftBodyView } from "./DraftBodyView";
import { BriefEditor } from "./BriefEditor";
import type { BriefEditorContextValue } from "./briefEditorContext";
import type { ContentBrief, ContentBriefDraft, ContentDestinationFormat } from "@/lib/composerCore";
import { contentDestinationFormatLabels } from "@/lib/composerCore";
import { PUBLISH_TYPES, type PublishTypeDef } from "@/lib/publishTypes";
import { runBriefPublicationPrep } from "@/lib/composer/core/pieces/runs";
import { clearApprovedBriefPlan } from "@/lib/composer/core/pieces/briefs";
import { createUserEditRevision, saveUserEditRevision, type ContentBriefDraftRevision } from "@/lib/briefRevisions";
import { composerBus } from "@/lib/composerBus";
import type { RevisionSaveState } from "./draftCanvasTypes";
import { BlankCanvas } from "./BlankCanvas";
import { DraftHeader } from "./DraftHeader";
import { EmptyState } from "./DraftCanvasParts";
import { useCanvasStream, type StreamTarget } from "./useCanvasStream";
import { useBriefDraftLiveStream } from "./useBriefDraftLiveStream";
import { StreamingBanner } from "./StreamingBanner";



// Advanced "raw format" escape hatch — kept for power users while the 6 Publish
// Types are the primary surface. See mem://features/publishing-adapter-registry.md.
const ADVANCED_RAW_FORMATS: ContentDestinationFormat[] = ["markdown", "html", "plaintext"];

export type DraftCanvasCommit = {
  draft: ContentBriefDraftRevision;
  draftId: string;
  briefId: string;
  doc: unknown;
  mode: "new" | "append" | "replace" | "insert";
  title?: string | null;
};

const sameDoc = (a: unknown, b: unknown) => {
  if (a === b) return true;
  try {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  } catch {
    return false;
  }
};

export type DraftCanvasProps = {
  workspaceId: string | null;
  brief: ContentBrief | null;
  latestDraft: ContentBriefDraft | null;
  onChange: (doc: unknown) => void;
  /** Called after the title is debounce-saved (so parent can refresh). */
  onTitleSaved?: (draftId: string, title: string) => void;
  /** Save status for the active user_edit draft, if any. */
  saveState?: RevisionSaveState;
  saveError?: string | null;
  onDraftPromoted?: (commit?: DraftCanvasCommit) => void;
  /** Switch the canvas back to a fresh blank-slate state. */
  onNewDraft?: () => void;
  /** Seed a new user_edit revision from the current read-only AI draft. */
  onSaveAsEditable?: () => void | Promise<void>;
  /** True while the save-as-editable request is in-flight. */
  savingAsEditable?: boolean;
  /** Delete the currently-open draft. Parent handles snapshot refresh and canvas navigation. */
  onDeleteDraft?: (draftId: string) => void | Promise<void>;
  /** Extra editor-context callbacks (e.g. notifyChanged) forwarded to BriefEditor node-views. */
  contextExtras?: Pick<BriefEditorContextValue, "notifyChanged">;
  /** Called after the toolbar's "Clear brief" button successfully clears the approved brief. */
  onApprovedBriefCleared?: () => void | Promise<void>;
};

export const DraftCanvas = ({
  workspaceId,
  brief,
  latestDraft,
  onChange,
  onTitleSaved,
  saveState,
  saveError,
  onDraftPromoted,
  onNewDraft,
  onSaveAsEditable,
  savingAsEditable,
  onDeleteDraft,
  contextExtras,
  onApprovedBriefCleared,
}: DraftCanvasProps) => {
  const navigate = useNavigate();
  // Keep a live copy of the doc in this component so the title placeholder can
  // reflect the user's typing without waiting for a parent save.
  const [liveDoc, setLiveDoc] = useState<unknown>(latestDraft?.draft_doc ?? null);
  useEffect(() => {
    setLiveDoc(latestDraft?.draft_doc ?? null);
  }, [latestDraft?.id, latestDraft?.draft_doc]);

  // "Clear brief" toolbar control — reflects `content_pieces.approved_brief_json`.
  const [clearingBrief, setClearingBrief] = useState(false);
  const hasApprovedBrief = Boolean(
    (brief as { approved_brief_json?: unknown } | null)?.approved_brief_json,
  );
  const approvedBriefControl = useMemo(() => {
    if (!brief || !workspaceId) return undefined;
    return {
      active: hasApprovedBrief,
      clearing: clearingBrief,
      onClear: async () => {
        if (!hasApprovedBrief || clearingBrief) return;
        setClearingBrief(true);
        try {
          await clearApprovedBriefPlan(workspaceId, brief.id);
          toast.success("Approved brief cleared", {
            description: "The Writing Agent will run without a brief handoff on the next generation.",
          });
          await onApprovedBriefCleared?.();
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Failed to clear brief");
        } finally {
          setClearingBrief(false);
        }
      },
    };
  }, [brief, workspaceId, hasApprovedBrief, clearingBrief, onApprovedBriefCleared]);

  // Active-draft Push to Canvas: stash is implicit (autosave already wrote the
  // current draft); create a NEW user_edit revision pre-loaded with the pushed doc.
  useEffect(() => {
    if (!latestDraft || !brief || !workspaceId) return;
    return composerBus.subscribe("draft:insert", async (payload) => {
      if (payload.mode !== "replace-clean") return;
      try {
        const rev = await createUserEditRevision({ workspaceId, briefId: brief.id, source: null });
        const saved = await saveUserEditRevision({
          draftId: rev.id,
          doc: payload.doc,
          title: payload.label ?? null,
        });
        toast.success("Pushed to a new draft", {
          description: "Previous draft is in the Drafts tab.",
        });
        onDraftPromoted?.({
          draft: saved,
          draftId: saved.id,
          briefId: brief.id,
          doc: payload.doc,
          mode: "insert",
          title: payload.label ?? null,
        });
      } catch (err) {
        toast.error((err as Error).message || "Push to canvas failed");
      }
    });
  }, [brief?.id, latestDraft?.id, workspaceId, onDraftPromoted, brief, latestDraft]);

  // Active-draft Open Piece: autosave already covers in-flight edits, so just ack.
  useEffect(() => {
    if (!brief || !workspaceId) return;
    return composerBus.subscribe("piece:open", (payload) => {
      const same = brief.id === payload.pieceId;
      toast.success(same ? `Already on "${payload.label ?? "this piece"}"` : `Opened "${payload.label ?? "piece"}"`);
      composerBus.emit("piece:open:ack", { pieceId: payload.pieceId, stashed: false });
    });
  }, [brief?.id, workspaceId, brief]);

  const isUserEdit = latestDraft?.revision_kind === "user_edit";
  const [savingNow, setSavingNow] = useState(false);
  const [pushingFormat, setPushingFormat] = useState<ContentDestinationFormat | null>(null);

  // Per-token canvas streaming from the AI Chat "Write to canvas" toggle.
  const handleStreamStart = useCallback(
    async ({ label, mode }: { label: string; mode: "new" | "append" | "replace" }): Promise<StreamTarget> => {
      if (!brief || !workspaceId) throw new Error("No active piece for streaming");
      if ((mode === "append" || mode === "replace") && latestDraft) {
        // saveUserEditRevision only writes to rows where revision_kind='user_edit'.
        // If the active draft is an AI revision, reusing its id would silently
        // no-op the final save. Spin up a new user_edit revision instead:
        //   - append → seed from the AI draft (createUserEditRevision converts
        //     the AI plain text to a BriefDocument), then use that doc as the seed
        //     so streamed chunks land after the existing text.
        //   - replace → fresh empty user_edit; the AI draft stays in Drafts tab.
        if (latestDraft.revision_kind === "user_edit") {
          const seed =
            mode === "append"
              ? (((latestDraft.draft_doc as { content?: unknown[] } | null)?.content) ?? [])
              : [];
          return {
            draftId: latestDraft.id,
            workspaceId,
            briefId: brief.id,
            label,
            seedContent: mode === "append" ? seed : undefined,
            mode,
          };
        }
        const rev = await createUserEditRevision({
          workspaceId,
          briefId: brief.id,
          source: mode === "append" ? latestDraft : null,
        });
        const seed =
          mode === "append"
            ? (((rev.draft_doc as { content?: unknown[] } | null)?.content) ?? [])
            : [];
        return {
          draftId: rev.id,
          workspaceId,
          briefId: brief.id,
          label,
          seedContent: mode === "append" ? seed : undefined,
          mode,
        };
      }
      // "new" (default) — or append/replace with no active draft to target.
      const rev = await createUserEditRevision({ workspaceId, briefId: brief.id, source: null });
      return { draftId: rev.id, workspaceId, briefId: brief.id, label, mode: "new" };
    },
    [brief, workspaceId, latestDraft],
  );

  const handleStreamEnd = useCallback(
    async ({
      target,
      doc,
    }: {
      target: StreamTarget;
      doc: unknown;
      aborted: boolean;
    }) => {
      // For append/replace we save into the existing draft in place; the
      // title stays as the user set it. For new-draft mode we set the label.
      const isInPlace = target.mode === "append" || target.mode === "replace";
      const saved = await saveUserEditRevision({
        draftId: target.draftId,
        doc,
        title: isInPlace ? undefined : target.label,
      });
      toast.success(
        target.mode === "append"
          ? "Appended to current draft"
          : target.mode === "replace"
            ? "Current draft updated"
            : "Excerptus AI draft saved",
        isInPlace
          ? undefined
          : { description: "Previous draft is in the Drafts tab." },
      );
      onDraftPromoted?.({
        draft: saved,
        draftId: target.draftId,
        briefId: target.briefId,
        doc,
        mode: target.mode ?? "new",
        title: isInPlace ? undefined : target.label,
      });
    },
    [onDraftPromoted],
  );
  const streamCtxExtras = contextExtras;
  const stream = useCanvasStream({
    enabled: Boolean(workspaceId && brief && latestDraft),
    onStart: handleStreamStart,
    onEnd: handleStreamEnd,
  });
  // Once the parent's refreshed `latestDraft` reflects the just-saved draft,
  // clear the settling stream so DraftBodyView takes over. Includes a 6s
  // fallback in case the parent refetch never completes.
  const { settling, acknowledge, target: streamTarget, doc: streamDoc } = stream;
  useEffect(() => {
    if (!settling) return;
    const targetId = streamTarget?.draftId;
    if (targetId && latestDraft?.id === targetId && sameDoc(latestDraft?.draft_doc, streamDoc)) {
      acknowledge();
      return;
    }
    const t = window.setTimeout(() => acknowledge(), 6000);
    return () => window.clearTimeout(t);
  }, [settling, streamTarget?.draftId, streamDoc, acknowledge, latestDraft?.id, latestDraft?.draft_doc]);
  const briefDraftLive = useBriefDraftLiveStream(brief?.id ?? null);

  const streamCtx: BriefEditorContextValue = useMemo(
    () => ({
      workspaceId: workspaceId ?? null,
      briefId: brief?.id ?? null,
      references: [],
      notifyChanged: streamCtxExtras?.notifyChanged,
    }),
    [workspaceId, brief?.id, streamCtxExtras?.notifyChanged],
  );


  const handleSaveNow = async () => {
    if (!isUserEdit || !latestDraft) return;
    setSavingNow(true);
    try {
      await saveUserEditRevision({ draftId: latestDraft.id, doc: liveDoc });
      toast.success("Draft saved");
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSavingNow(false);
    }
  };

  const handlePushToPublisher = async (
    format: ContentDestinationFormat,
    options: { publishTypeKey?: string | null; label?: string } = {},
  ) => {
    if (!brief || !workspaceId || !latestDraft) return;
    setPushingFormat(format);
    try {
      // Flush latest edits first so the publisher prep uses fresh content.
      if (isUserEdit) {
        await saveUserEditRevision({ draftId: latestDraft.id, doc: liveDoc });
      }
      await runBriefPublicationPrep(workspaceId, {
        briefId: brief.id,
        draftId: latestDraft.id,
        destinationFormat: format,
        publishTypeKey: options.publishTypeKey ?? null,
      });
      const friendly = options.label ?? contentDestinationFormatLabels[format];
      toast.success(`Pushed to Publisher — ${friendly}`, {
        action: { label: "Open Publisher", onClick: () => navigate("/publisher") },
      });
    } catch (err) {
      toast.error((err as Error).message || "Push to Publisher failed");
    } finally {
      setPushingFormat(null);
    }
  };

  const handlePushPublishType = (def: PublishTypeDef) =>
    void handlePushToPublisher(def.destinationFormat, { publishTypeKey: def.key, label: def.label });

  if (!workspaceId) {
    return (
      <EmptyState
        message="Pick a workspace to start composing"
        hint="The Composer canvas opens once a workspace is active."
      />
    );
  }

  if (!latestDraft) {
    return (
      <BlankCanvas
        workspaceId={workspaceId}
        existingBrief={brief}
        onPromoted={onDraftPromoted}
      />
    );
  }



  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <DraftHeader
        draft={latestDraft}
        brief={brief}
        saveState={saveState}
        saveError={saveError}
        onTitleSaved={onTitleSaved}
        latestDoc={liveDoc}
        onNewDraft={onNewDraft}
        onSaveAsEditable={onSaveAsEditable}
        savingAsEditable={savingAsEditable}
        extraActions={
          <>
            {isUserEdit ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveNow}
                disabled={savingNow}
                aria-label={savingNow ? "Saving draft" : "Save draft"}
                title={savingNow ? "Saving…" : "Save draft"}
              >
                {savingNow ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <SaveIcon className="h-3.5 w-3.5" />
                )}
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="default" disabled={!brief || pushingFormat !== null}>
                  <Send className="mr-1 h-3.5 w-3.5" />
                  {pushingFormat ? "Pushing…" : "Push to Publisher"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Publish as…</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PUBLISH_TYPES.map((def) => (
                  <DropdownMenuItem
                    key={def.key}
                    onClick={() => handlePushPublishType(def)}
                    disabled={pushingFormat !== null}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="font-medium">{def.label}</span>
                    <span className="text-xs text-muted-foreground">{def.description}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                  Advanced — raw format
                </DropdownMenuLabel>
                {ADVANCED_RAW_FORMATS.map((fmt) => (
                  <DropdownMenuItem
                    key={fmt}
                    onClick={() => void handlePushToPublisher(fmt)}
                    disabled={pushingFormat !== null}
                    className="text-xs"
                  >
                    {contentDestinationFormatLabels[fmt]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {onDeleteDraft ? (
              <Button
                size="sm"
                variant="outline"
                className="text-muted-foreground hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                aria-label="Delete draft"
                title="Delete draft"
                onClick={() => {
                  const label = latestDraft.draft_title?.trim() || brief?.title || "this draft";
                  const ok = window.confirm(`Delete draft "${label}"? This can't be undone.`);
                  if (!ok) return;
                  void onDeleteDraft(latestDraft.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </>
        }
      />
      <div className="min-h-0 flex-1 flex flex-col gap-2">
        {stream.active ? <StreamingBanner label={stream.target?.label ?? "Excerptus AI draft"} /> : null}
        {briefDraftLive.active && !stream.active ? (
          <StreamingBanner label={briefDraftLive.label ?? "Drafting from brief…"} />
        ) : null}
        <div className="min-h-0 flex-1 flex flex-col">
          {stream.active ? (
            <BriefEditor
              value={stream.doc ?? { type: "doc", content: [{ type: "paragraph" }] }}
              streamContent={stream.doc}
              onChange={() => {
                /* no-op: editor is locked during stream */
              }}
              context={streamCtx}
              editable={false}
              workspaceId={workspaceId}
              placeholder="Excerptus AI writing…"
            />
          ) : briefDraftLive.active ? (
            <BriefEditor
              value={briefDraftLive.doc ?? { type: "doc", content: [{ type: "paragraph" }] }}
              streamContent={briefDraftLive.doc}
              onChange={() => {
                /* no-op: editor is locked during brief draft stream */
              }}
              context={streamCtx}
              editable={false}
              workspaceId={workspaceId}
              placeholder="Drafting…"
            />
          ) : (
            <DraftBodyView
              draft={latestDraft}
              workspaceId={workspaceId}
              briefId={brief?.id ?? latestDraft.piece_id}
              contextExtras={contextExtras}
              approvedBriefControl={approvedBriefControl}
              onChange={(doc) => {
                setLiveDoc(doc);
                onChange(doc);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DraftCanvas;

